<template>
  <div
    :data-id="purchase.id"
    class="content-row"
    :style="{ '--row-height': lineHeightsType.height + 'px' }"
    :class="[
      `lines-${lineHeightsType.id}`,
      {
        'is-child': purchase.parentId,
        'is-first-child': purchase.isFirstChild,
        'is-updated': purchase.isUpdated,
        'is-duplicated': purchase.isDuplicated,
        'is-new': purchase.isNew && highlightNew,
        'is-last-row': purchase.isLastRow,
        'is-collapsed': purchase.isCollapsed,
        'table-row-enter': animation === 'enter',
        'table-row-leave': animation === 'leave',
      },
    ]"
  >
    <div
      v-for="column in columns"
      :key="column.field"
      class="cell"
      :class="{ 'tree-cell': column.field === 'hidden' }"
      :style="column.style"
      :data-field="column.field"
    >
      <div class="cell-content">
        <component
          v-if="column.cell?.component"
          :is="column.cell.component"
          :value="purchase.values[column.field]"
          :row="purchase"
          :column="column"
          :field="column.field"
        />

        <span v-else-if="column.field === 'organization'">
          {{ purchase.values[column.field].split(";").join(";\u00A0") }}
        </span>

        <span v-else v-once>
          {{ purchase.values[column.field] }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PurchaseTreeTableRow",
  props: {
    purchase: { type: Object, required: true },
    columns: { type: Array, required: true },
    highlightNew: { type: Boolean, default: false },
    lineHeightsType: { type: Object, required: true },
    animation: { type: String, default: null },
  },
};
</script>

<style scoped lang="less">
.table-row-enter {
  animation: rowEnter 0.3s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  transform-origin: top;
  will-change: transform, opacity;
}

.table-row-leave {
  animation: rowLeave 0.3s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  transform-origin: top;
  will-change: transform, opacity;
}

@keyframes rowLeave {
  from {
    transform: scaleY(1);
    opacity: 1;
  }
  to {
    transform: scaleY(0);
    opacity: 0;
  }
}

@keyframes rowEnter {
  from {
    transform: scaleY(0);
    opacity: 0;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

.content-row {
  display: flex;
  flex-wrap: nowrap;
  min-width: max-content;
  max-width: max-content;
  transition: background-color 0.2s ease;
  position: relative;
  font-size: 13px;
  border-bottom: 2px solid white;
  box-sizing: border-box;
  height: var(--row-height);
  --row-bg: white;

  &.is-updated {
    background-color: rgb(255, 240, 218);
    --row-bg: rgb(255, 240, 218);
  }
  &.is-duplicated {
    background-color: rgb(255, 211, 143);
    --row-bg: rgb(255, 211, 143);
  }
  &.is-new {
    background-color: rgb(237, 239, 241);
    --row-bg: rgb(237, 239, 241);
  }
  &:hover {
    background-color: rgb(246, 247, 248);
    --row-bg: rgb(246, 247, 248);
  }
}

.content-row:hover {
  ::v-deep .notes-btn {
    opacity: 1;
  }
}

.content-row::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: rgb(237, 239, 241);
  left: calc(32px + 14px + 14px);
  z-index: 20;
}

.content-row:not(.is-new)::after {
  transition: left 0.35s ease;
}

.content-row.is-last-row::after,
.content-row.is-collapsed::after,
.content-row.is-new::after {
  left: 0px;
}

.content-row.is-child .tree-cell::before {
  content: "";
  width: 10px;
  height: calc(var(--row-height) + 6px);
  position: absolute;
  left: calc(11px + 14px + 18px);
  border-left: 1px solid #cfd8dd;
  border-bottom: 1px solid #cfd8dd;
  border-bottom-left-radius: 4px;
}

.content-row[style*="--row-height: 48px"] .tree-cell::before {
  top: -29px;
}

.content-row[style*="--row-height: 62px"] .tree-cell::before {
  top: -36px;
}

.content-row[style*="--row-height: 93px"] .tree-cell::before {
  top: -52px;
}

.content-row[style*="--row-height: 48px"].is-child.is-first-child
  .tree-cell::before {
  top: -17px;
  height: calc(var(--row-height) - 6px);
}

.content-row[style*="--row-height: 62px"].is-child.is-first-child
  .tree-cell::before {
  top: -24px;
  height: calc(var(--row-height) - 6px);
}

.content-row[style*="--row-height: 93px"].is-child.is-first-child
  .tree-cell::before {
  top: -40px;
  height: calc(var(--row-height) - 6px);
}

.cell {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 8px 4px;
  overflow: hidden;
  background-color: var(--row-bg);
  color: rgb(55, 71, 79);
}

.cell:nth-child(1) {
  position: sticky;
  left: 0;
  z-index: 3;
  overflow: visible;
}

.cell:nth-child(2) {
  position: sticky;
  left: calc(52px + 14px + 14px);
  z-index: 3;
  .cell-content {
    width: 100%;
    height: 100%;
  }
}

.cell:nth-child(-n + 2) {
  padding: 0;
}

.cell-content {
  overflow: hidden;
  display: -webkit-box;
  white-space: normal;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  line-height: 22px;
}

.content-row.lines-1 .cell-content {
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.content-row.lines-2 .cell-content {
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.content-row.lines-3 .cell-content {
  -webkit-line-clamp: 3;
  line-clamp: 3;
}
</style>
